import { type PlateContent, type Value } from '@udecode/plate-common';
import * as XLSX from 'xlsx';
import { downloadFile } from '@/lib/utils';
import { getChaptersByStoryId, getChapterContent } from '@/services/supabase/chapters';
import { getStoriesByProject } from '@/services/supabase/stories';
import { serializeHtml } from '@udecode/plate/react';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import JSZip from 'jszip';

export interface ExportOptions {
  title: string;
  author?: string;
  includeStoryInfo?: boolean;
  includeChapterTitles?: boolean;
  includeTOC?: boolean;
  pageSize?: 'A4' | 'Letter' | 'Legal';
  font?: string;
  fontSize?: number;
  lineSpacing?: number;
  paragraphSpacing?: number;
  includePageNumbers?: boolean;
  format: 'markdown' | 'html' | 'docx' | 'text' | 'pdf' | 'epub' | 'odt';
}

/**
 * Simple text extraction for markdown-like output
 * @param content - The Plate editor content
 * @returns Markdown-like string
 */
export const exportToMarkdown = (content: PlateContent): string => {
  return content.map(block => {
    if (block.children) {
      const text = block.children.map((child: any) => child.text || '').join('');
      if (block.type === 'h1') return `# ${text}\n\n`;
      if (block.type === 'h2') return `## ${text}\n\n`;
      if (block.type === 'h3') return `### ${text}\n\n`;
      if (block.type === 'blockquote') return `> ${text}\n\n`;
      return text + '\n\n';
    }
    return '';
  }).join('');
};

/**
 * Export chapter content as HTML
 * @param content - The Plate editor content
 * @returns HTML string
 */
export const exportToHtml = (content: PlateContent): string => {
  // Simple HTML conversion - this is a basic implementation
  return content.map(block => {
    if (block.children) {
      const text = block.children.map((child: any) => child.text || '').join('');
      if (block.type === 'h1') return `<h1>${text}</h1>`;
      if (block.type === 'h2') return `<h2>${text}</h2>`;
      if (block.type === 'h3') return `<h3>${text}</h3>`;
      if (block.type === 'blockquote') return `<blockquote>${text}</blockquote>`;
      return `<p>${text}</p>`;
    }
    return '';
  }).join('\n');
};

/**
 * Export chapter content as plain text
 * @param content - The Plate editor content
 * @returns Plain text string
 */
export const exportToText = (content: PlateContent): string => {
  // Simple text extraction - could be improved for more complex documents
  return content.map(block => {
    if (block.children) {
      return block.children.map((child: any) => child.text || '').join('');
    }
    return '';
  }).join('\n\n');
};

/**
 * Export chapter content as DOCX
 * @param content - The Plate editor content
 * @param options - Export options
 * @returns Blob containing DOCX file
 */
// Convert content to DOCX paragraphs
const convertContentToDocxParagraphs = (content: PlateContent, options: ExportOptions): Paragraph[] => {
  const paragraphs: Paragraph[] = [];

  // Add title if provided
  if (options.title) {
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: options.title,
            bold: true,
            size: 48, // 24pt
          }),
        ],
        alignment: 'center',
        spacing: { after: 480 },
      })
    );
  }

  // Add author if provided
  if (options.author) {
    paragraphs.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `By ${options.author}`,
            size: 28, // 14pt
          }),
        ],
        alignment: 'center',
        spacing: { after: 720 },
      })
    );
  }

  // Convert content blocks
  content.forEach(block => {
    if (block.children) {
      const text = block.children.map((child: any) => child.text || '').join('');

      if (block.type === 'h1') {
        paragraphs.push(
          new Paragraph({
            text: text,
            heading: HeadingLevel.HEADING_1,
            spacing: { after: 240, before: 480 },
          })
        );
      } else if (block.type === 'h2') {
        paragraphs.push(
          new Paragraph({
            text: text,
            heading: HeadingLevel.HEADING_2,
            spacing: { after: 200, before: 400 },
          })
        );
      } else if (block.type === 'h3') {
        paragraphs.push(
          new Paragraph({
            text: text,
            heading: HeadingLevel.HEADING_3,
            spacing: { after: 160, before: 320 },
          })
        );
      } else if (block.type === 'blockquote') {
        paragraphs.push(
          new Paragraph({
            children: [
              new TextRun({
                text: text,
                italics: true,
              }),
            ],
            indent: { left: 720 },
            spacing: { after: 200 },
          })
        );
      } else if (text.trim()) {
        paragraphs.push(
          new Paragraph({
            children: [
              new TextRun({
                text: text,
              }),
            ],
            spacing: { after: 200 },
          })
        );
      }
    }
  });

  return paragraphs;
};

export const exportToDocx = async (content: PlateContent, options: ExportOptions): Promise<Blob> => {
  const paragraphs = convertContentToDocxParagraphs(content, options);

  // Create a new document
  const doc = new Document({
    sections: [
      {
        properties: {},
        children: paragraphs,
      },
    ],
  });

  // Generate and return the document as a blob
  const blob = await Packer.toBlob(doc);

  return blob;
};

/**
 * Export content as EPUB
 * @param content - The Plate editor content
 * @param options - Export options
 * @returns Blob containing EPUB file
 */
export const exportToEpub = async (content: PlateContent, options: ExportOptions): Promise<Blob> => {
  const html = exportToHtml(content);

  // Create EPUB structure
  const zip = new JSZip();

  // Add mimetype file
  zip.file('mimetype', 'application/epub+zip');

  // Add META-INF directory
  const metaInf = zip.folder('META-INF');
  metaInf?.file('container.xml', `<?xml version="1.0"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles>
    <rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/>
  </rootfiles>
</container>`);

  // Add OEBPS directory
  const oebps = zip.folder('OEBPS');

  // Add content.opf
  oebps?.file('content.opf', `<?xml version="1.0" encoding="UTF-8"?>
<package xmlns="http://www.idpf.org/2007/opf" unique-identifier="BookId" version="2.0">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:opf="http://www.idpf.org/2007/opf">
    <dc:title>${options.title || 'Exported Story'}</dc:title>
    <dc:language>en</dc:language>
    <dc:identifier id="BookId">story-export</dc:identifier>
    <dc:creator>${options.author || 'Plot Studio'}</dc:creator>
  </metadata>
  <manifest>
    <item id="ncx" href="toc.ncx" media-type="application/x-dtbncx+xml"/>
    <item id="content" href="content.xhtml" media-type="application/xhtml+xml"/>
    <item id="stylesheet" href="styles.css" media-type="text/css"/>
  </manifest>
  <spine toc="ncx">
    <itemref idref="content"/>
  </spine>
</package>`);

  // Add toc.ncx
  oebps?.file('toc.ncx', `<?xml version="1.0" encoding="UTF-8"?>
<ncx xmlns="http://www.daisy.org/z3986/2005/ncx/" version="2005-1">
  <head>
    <meta name="dtb:uid" content="story-export"/>
    <meta name="dtb:depth" content="1"/>
    <meta name="dtb:totalPageCount" content="0"/>
    <meta name="dtb:maxPageNumber" content="0"/>
  </head>
  <docTitle>
    <text>${options.title || 'Exported Story'}</text>
  </docTitle>
  <navMap>
    <navPoint id="navpoint-1" playOrder="1">
      <navLabel>
        <text>Story</text>
      </navLabel>
      <content src="content.xhtml"/>
    </navPoint>
  </navMap>
</ncx>`);

  // Add styles.css
  oebps?.file('styles.css', `
body {
  font-family: ${options.font || 'Georgia'}, serif;
  font-size: ${options.fontSize || 12}pt;
  line-height: ${options.lineSpacing || 1.6};
  margin: 2em;
}
h1, h2, h3 {
  color: #333;
  margin-top: 1.5em;
  margin-bottom: 0.5em;
}
p {
  margin-bottom: ${options.paragraphSpacing || 1.2}em;
  text-align: justify;
}
blockquote {
  margin: 1em 2em;
  font-style: italic;
  border-left: 3px solid #ccc;
  padding-left: 1em;
}
.title {
  font-size: 24pt;
  font-weight: bold;
  text-align: center;
  margin-bottom: 2em;
}
.author {
  font-size: 14pt;
  text-align: center;
  margin-bottom: 3em;
}
`);

  // Add content
  oebps?.file('content.xhtml', `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.1//EN" "http://www.w3.org/TR/xhtml11/DTD/xhtml11.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
  <title>${options.title || 'Exported Story'}</title>
  <link rel="stylesheet" type="text/css" href="styles.css"/>
</head>
<body>
  ${options.title ? `<div class="title">${options.title}</div>` : ''}
  ${options.author ? `<div class="author">By ${options.author}</div>` : ''}
  ${html}
</body>
</html>`);

  // Generate EPUB file
  const epubBlob = await zip.generateAsync({ type: 'blob' });
  return epubBlob;
};

/**
 * Export content as ODT
 * @param content - The Plate editor content
 * @param options - Export options
 * @returns Blob containing ODT file
 */
export const exportToOdt = async (content: PlateContent, options: ExportOptions): Promise<Blob> => {
  // Create ODT structure
  const zip = new JSZip();

  // Add mimetype
  zip.file('mimetype', 'application/vnd.oasis.opendocument.text');

  // Add META-INF directory
  const metaInf = zip.folder('META-INF');
  metaInf?.file('manifest.xml', `<?xml version="1.0" encoding="UTF-8"?>
<manifest:manifest xmlns:manifest="urn:oasis:names:tc:opendocument:xmlns:manifest:1.0" manifest:version="1.2">
  <manifest:file-entry manifest:full-path="/" manifest:version="1.2" manifest:media-type="application/vnd.oasis.opendocument.text"/>
  <manifest:file-entry manifest:full-path="content.xml" manifest:media-type="text/xml"/>
  <manifest:file-entry manifest:full-path="styles.xml" manifest:media-type="text/xml"/>
  <manifest:file-entry manifest:full-path="meta.xml" manifest:media-type="text/xml"/>
</manifest:manifest>`);

  // Add meta.xml
  zip.file('meta.xml', `<?xml version="1.0" encoding="UTF-8"?>
<office:document-meta xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" xmlns:meta="urn:oasis:names:tc:opendocument:xmlns:meta:1.0" xmlns:dc="http://purl.org/dc/elements/1.1/" office:version="1.2">
  <office:meta>
    <meta:generator>Plot Studio</meta:generator>
    <meta:creation-date>${new Date().toISOString()}</meta:creation-date>
    <dc:title>${options.title || 'Exported Story'}</dc:title>
    <dc:creator>${options.author || 'Plot Studio'}</dc:creator>
    <dc:language>en-US</dc:language>
  </office:meta>
</office:document-meta>`);

  // Add styles.xml
  zip.file('styles.xml', `<?xml version="1.0" encoding="UTF-8"?>
<office:document-styles xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" xmlns:style="urn:oasis:names:tc:opendocument:xmlns:style:1.0" xmlns:fo="urn:oasis:names:tc:opendocument:xmlns:xsl-fo-compatible:1.0" office:version="1.2">
  <office:styles>
    <style:default-style style:family="paragraph">
      <style:paragraph-properties fo:text-align="justify"/>
      <style:text-properties style:font-name="${options.font || 'Georgia'}" fo:font-size="${options.fontSize || 12}pt"/>
    </style:default-style>
    <style:style style:name="Heading_20_1" style:display-name="Heading 1" style:family="paragraph" style:parent-style-name="Heading" style:next-style-name="Text_20_body">
      <style:paragraph-properties fo:margin-top="0.423cm" fo:margin-bottom="0.212cm" fo:keep-with-next="always"/>
      <style:text-properties fo:font-size="18pt" fo:font-weight="bold"/>
    </style:style>
    <style:style style:name="Heading_20_2" style:display-name="Heading 2" style:family="paragraph" style:parent-style-name="Heading" style:next-style-name="Text_20_body">
      <style:paragraph-properties fo:margin-top="0.353cm" fo:margin-bottom="0.176cm" fo:keep-with-next="always"/>
      <style:text-properties fo:font-size="16pt" fo:font-weight="bold"/>
    </style:style>
    <style:style style:name="Heading_20_3" style:display-name="Heading 3" style:family="paragraph" style:parent-style-name="Heading" style:next-style-name="Text_20_body">
      <style:paragraph-properties fo:margin-top="0.247cm" fo:margin-bottom="0.176cm" fo:keep-with-next="always"/>
      <style:text-properties fo:font-size="14pt" fo:font-weight="bold"/>
    </style:style>
    <style:style style:name="Title" style:family="paragraph" style:parent-style-name="Heading" style:next-style-name="Text_20_body">
      <style:paragraph-properties fo:text-align="center" fo:margin-top="0.423cm" fo:margin-bottom="0.212cm"/>
      <style:text-properties fo:font-size="24pt" fo:font-weight="bold"/>
    </style:style>
    <style:style style:name="Author" style:family="paragraph" style:next-style-name="Text_20_body">
      <style:paragraph-properties fo:text-align="center" fo:margin-bottom="0.635cm"/>
      <style:text-properties fo:font-size="14pt"/>
    </style:style>
  </office:styles>
</office:document-styles>`);

  // Convert content to ODT format
  const convertToOdtContent = (content: PlateContent, options: ExportOptions): string => {
    let odtContent = '';

    // Add title if provided
    if (options.title) {
      odtContent += `<text:h text:style-name="Title" text:outline-level="1">${options.title}</text:h>\n`;
    }

    // Add author if provided
    if (options.author) {
      odtContent += `<text:p text:style-name="Author">By ${options.author}</text:p>\n`;
    }

    // Convert content blocks
    content.forEach(block => {
      if (block.children) {
        const text = block.children.map((child: any) => child.text || '').join('');
        if (block.type === 'h1') {
          odtContent += `<text:h text:style-name="Heading_20_1" text:outline-level="1">${text}</text:h>\n`;
        } else if (block.type === 'h2') {
          odtContent += `<text:h text:style-name="Heading_20_2" text:outline-level="2">${text}</text:h>\n`;
        } else if (block.type === 'h3') {
          odtContent += `<text:h text:style-name="Heading_20_3" text:outline-level="3">${text}</text:h>\n`;
        } else if (block.type === 'blockquote') {
          odtContent += `<text:p><text:span text:style-name="Emphasis">${text}</text:span></text:p>\n`;
        } else if (text.trim()) {
          odtContent += `<text:p>${text}</text:p>\n`;
        }
      }
    });

    return odtContent;
  };

  const odtContent = convertToOdtContent(content, options);

  // Add content.xml
  zip.file('content.xml', `<?xml version="1.0" encoding="UTF-8"?>
<office:document-content xmlns:office="urn:oasis:names:tc:opendocument:xmlns:office:1.0" xmlns:text="urn:oasis:names:tc:opendocument:xmlns:text:1.0" office:version="1.2">
  <office:body>
    <office:text>
      ${odtContent}
    </office:text>
  </office:body>
</office:document-content>`);

  // Generate ODT file
  const odtBlob = await zip.generateAsync({ type: 'blob' });
  return odtBlob;
};

/**
 * Export an entire story
 * @param storyId - The ID of the story
 * @param options - Export options
 */
export const exportStory = async (storyId: number, options: ExportOptions): Promise<void> => {
  try {
    // Get all chapters for the story
    const chapters = await getChaptersByStoryId(storyId);

    // Sort chapters by ID or another field if available
    const sortedChapters = [...chapters].sort((a, b) => a.id - b.id);

    // Collect all chapter content
    let allContent: PlateContent = [];
    let allHtml = '';
    let allMarkdown = '';
    let allText = '';

    for (const chapter of sortedChapters) {
      // Get chapter content
      const chapterContent = await getChapterContent(chapter.id);

      if (chapterContent && chapterContent.content) {
        const content = chapterContent.content;

        // Add chapter title if required
        if (options.includeChapterTitles && chapter.title) {
          if (options.format === 'html') {
            allHtml += `<h2 class="chapter-title">${chapter.title}</h2>\n`;
          } else if (options.format === 'markdown') {
            allMarkdown += `## ${chapter.title}\n\n`;
          } else if (options.format === 'text') {
            allText += `${chapter.title.toUpperCase()}\n\n`;
          }

          // For DOCX/PDF we add a chapter title element to the content
          if (options.format === 'docx' || options.format === 'pdf') {
            allContent.push({
              type: 'h2',
              children: [{ text: chapter.title }],
              align: 'center',
              class: 'chapter-title'
            });
          }
        }

        // Add content based on format
        if (options.format === 'html') {
          allHtml += exportToHtml(content) + '\n';
        } else if (options.format === 'markdown') {
          allMarkdown += exportToMarkdown(content) + '\n\n';
        } else if (options.format === 'text') {
          allText += exportToText(content) + '\n\n';
        } else {
          // For DOCX/PDF, we collect all content
          allContent = [...allContent, ...content];
        }
      }
    }

    // Generate and download the file based on format
    const filename = `${options.title || 'Story'}.${options.format === 'text' ? 'txt' : options.format}`;

    if (options.format === 'html') {
      // Wrap HTML in a complete document
      const fullHtml = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>${options.title || 'Exported Story'}</title>
          <style>
            body {
              font-family: ${options.font || 'Georgia'}, serif;
              font-size: ${options.fontSize || 12}pt;
              line-height: ${options.lineSpacing || 1.5};
              margin: 2em;
            }
            h1, h2, h3, h4, h5, h6 {
              margin-top: ${options.paragraphSpacing || 1.2}em;
              margin-bottom: 0.5em;
            }
            p {
              margin-bottom: ${options.paragraphSpacing || 1.2}em;
              text-align: justify;
            }
            .title {
              font-size: 24pt;
              font-weight: bold;
              text-align: center;
              margin-bottom: 2em;
            }
            .author {
              font-size: 14pt;
              text-align: center;
              margin-bottom: 3em;
            }
            .chapter-title {
              font-size: 18pt;
              font-weight: bold;
              text-align: center;
              margin-top: 2em;
              margin-bottom: 1.5em;
            }
          </style>
        </head>
        <body>
          ${options.title ? `<div class="title">${options.title}</div>` : ''}
          ${options.author ? `<div class="author">By ${options.author}</div>` : ''}
          ${allHtml}
        </body>
        </html>
      `;

      const blob = new Blob([fullHtml], { type: 'text/html' });
      downloadFile(blob, filename);
    } else if (options.format === 'markdown') {
      // Add title and author for markdown
      const fullMarkdown = `# ${options.title || 'Exported Story'}\n\n` +
        (options.author ? `By: ${options.author}\n\n` : '') +
        allMarkdown;

      const blob = new Blob([fullMarkdown], { type: 'text/markdown' });
      downloadFile(blob, filename);
    } else if (options.format === 'text') {
      // Add title and author for plain text
      const fullText = `${options.title ? options.title.toUpperCase() : 'EXPORTED STORY'}\n\n` +
        (options.author ? `By: ${options.author}\n\n` : '') +
        allText;

      const blob = new Blob([fullText], { type: 'text/plain' });
      downloadFile(blob, filename);
    } else if (options.format === 'docx') {
      // Export as DOCX
      const docxBlob = await exportToDocx(allContent, options);
      downloadFile(docxBlob, filename);
    } else if (options.format === 'epub') {
      // Export as EPUB
      const epubBlob = await exportToEpub(allContent, options);
      downloadFile(epubBlob, filename);
    } else if (options.format === 'odt') {
      // Export as ODT
      const odtBlob = await exportToOdt(allContent, options);
      downloadFile(odtBlob, filename);
    }
  } catch (error) {
    console.error('Error exporting story:', error);
    throw error;
  }
};

/**
 * Export an entire project (all stories and chapters)
 * @param projectId - The ID of the project
 * @param options - Export options
 */
export const exportProject = async (projectId: string, options: ExportOptions): Promise<void> => {
  try {
    // Get all stories for the project
    const stories = await getStoriesByProject(projectId);

    if (!stories || stories.length === 0) {
      throw new Error('No stories found in this project');
    }

    // Sort stories by creation date or name
    const sortedStories = [...stories].sort((a, b) => a.name.localeCompare(b.name));

    // Collect all content from all stories
    let allContent: PlateContent = [];
    let allHtml = '';
    let allMarkdown = '';
    let allText = '';

    // Add project title as main heading
    const projectTitleNode = {
      type: 'h1',
      children: [{ text: options.title }]
    };
    allContent.push(projectTitleNode);

    for (const story of sortedStories) {
      // Add story title as section heading
      if (options.includeChapterTitles) {
        const storyTitleNode = {
          type: 'h2',
          children: [{ text: story.name }]
        };
        allContent.push(storyTitleNode);

        // Add story description if available
        if (story.plotline) {
          const storyDescNode = {
            type: 'p',
            children: [{ text: story.plotline }]
          };
          allContent.push(storyDescNode);
        }
      }

      // Get all chapters for this story
      const chapters = await getChaptersByStoryId(Number(story.id));

      if (chapters && chapters.length > 0) {
        // Sort chapters by ID
        const sortedChapters = [...chapters].sort((a, b) => a.id - b.id);

        for (const chapter of sortedChapters) {
          // Add chapter title
          if (options.includeChapterTitles) {
            const chapterTitleNode = {
              type: 'h3',
              children: [{ text: chapter.title }]
            };
            allContent.push(chapterTitleNode);
          }

          // Get chapter content
          const chapterContent = await getChapterContent(chapter.id);

          if (chapterContent && Array.isArray(chapterContent)) {
            // Add chapter content
            allContent.push(...chapterContent);
          }

          // Add spacing between chapters
          allContent.push({
            type: 'p',
            children: [{ text: '' }]
          });
        }
      }

      // Add spacing between stories
      allContent.push({
        type: 'p',
        children: [{ text: '' }]
      });
      allContent.push({
        type: 'p',
        children: [{ text: '' }]
      });
    }

    // Generate filename
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `${options.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_project_${timestamp}.${options.format}`;

    // Export based on format
    if (options.format === 'markdown') {
      // Convert to markdown
      allMarkdown = exportToMarkdown(allContent);
      const blob = new Blob([allMarkdown], { type: 'text/markdown' });
      downloadFile(blob, filename);
    } else if (options.format === 'html') {
      // Convert to HTML
      allHtml = exportToHtml(allContent);
      const fullHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${options.title}</title>
  <style>
    body { font-family: ${options.font || 'Georgia, serif'}; font-size: ${options.fontSize || 12}pt; line-height: ${options.lineSpacing || 1.5}; max-width: 800px; margin: 0 auto; padding: 20px; }
    h1 { color: #333; border-bottom: 2px solid #333; padding-bottom: 10px; }
    h2 { color: #555; border-bottom: 1px solid #555; padding-bottom: 5px; margin-top: 30px; }
    h3 { color: #777; margin-top: 25px; }
    p { margin-bottom: ${options.paragraphSpacing || 1.2}em; }
  </style>
</head>
<body>
${allHtml}
</body>
</html>`;
      const blob = new Blob([fullHtml], { type: 'text/html' });
      downloadFile(blob, filename);
    } else if (options.format === 'text') {
      // Convert to plain text
      allText = exportToText(allContent);
      const blob = new Blob([allText], { type: 'text/plain' });
      downloadFile(blob, filename);
    } else if (options.format === 'docx') {
      // Export as DOCX
      const docxBlob = await exportToDocx(allContent, options);
      downloadFile(docxBlob, filename);
    } else if (options.format === 'epub') {
      // Export as EPUB
      const epubBlob = await exportToEpub(allContent, options);
      downloadFile(epubBlob, filename);
    } else if (options.format === 'odt') {
      // Export as ODT
      const odtBlob = await exportToOdt(allContent, options);
      downloadFile(odtBlob, filename);
    }
  } catch (error) {
    console.error('Error exporting project:', error);
    throw error;
  }
};